const BLOCKED_LETTERS = new Set<string>([]);

function normalize(value: string | null | undefined): string {
  return (value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function hasBlockedLetter(value: string): boolean {
  return value.split("").some((char) => BLOCKED_LETTERS.has(char));
}

function charToValue(char: string): number {
  return char.charCodeAt(0) - 48;
}

function calculateDigit(value: string, weights: number[]): number {
  const sum = value
    .split("")
    .reduce((acc, current, index) => acc + charToValue(current) * weights[index], 0);

  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

function formatCnpj(value: string): string {
  const cnpj = normalize(value).slice(0, 14);
  if (!cnpj) return "";

  const part1 = cnpj.slice(0, 2);
  const part2 = cnpj.slice(2, 5);
  const part3 = cnpj.slice(5, 8);
  const part4 = cnpj.slice(8, 12);
  const part5 = cnpj.slice(12, 14);

  let masked = part1;
  if (part2) masked += `.${part2}`;
  if (part3) masked += `.${part3}`;
  if (part4) masked += `/${part4}`;
  if (part5) masked += `-${part5}`;

  return masked;
}

export default class CNPJ {
  public static isValid(cnpj: string) {
    const normalized = normalize(cnpj);

    if (!normalized || normalized.length !== 14) return false;

    if (hasBlockedLetter(normalized)) return false;

    if ([...new Set(normalized)].length === 1) return false;

    const base = normalized.slice(0, 12);
    const verifierDigits = normalized.slice(12);

    if (!/^\d{2}$/.test(verifierDigits)) return false;

    const firstDigit = calculateDigit(base, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
    const secondDigit = calculateDigit(
      `${base}${firstDigit}`,
      [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
    );

    return verifierDigits === `${firstDigit}${secondDigit}`;
  }

  public static mask(cnpj: string) {
    if (cnpj === null || cnpj === undefined) {
      return "";
    }

    return formatCnpj(cnpj);
  }

  public static cnpjHideMask(cnpj: string): string {
    if (cnpj === null || cnpj === undefined) {
      return "";
    }

    const cleaned = normalize(cnpj);

    if (cleaned.length === 14) {
      return `**.***.***/${cleaned.slice(8, 12)}-${cleaned.slice(12, 14)}`;
    }

    return cleaned;
  }

  public static clear(cnpj: string): string {
    if (cnpj === null || cnpj === undefined) {
      return "";
    }

    const normalized = normalize(cnpj).slice(0, 14);
    return normalized
      .split("")
      .filter((char) => !BLOCKED_LETTERS.has(char))
      .join("");
  }

  public static sanitize(cnpj: string): string {
    return CNPJ.clear(cnpj);
  }
}
